import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Container,
  Button,
  Stack,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";

import CloseIcon from "@mui/icons-material/Close";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../static/media/logo.webp";
const MotionButton = motion(Button);

export default function Navbar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (sectionId) => {
    setDrawerOpen(false);
    if (!sectionId) {
      goHome();
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 120);
    } else {
      document.getElementById(sectionId)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const goHome = () => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 120);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: "transparent",
          boxShadow: "none",
          top: 16,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            sx={{
              minHeight: 76,
              px: { xs: 2, md: 3 },

              background: "rgba(255,255,255,0.82)",
              backdropFilter: "blur(18px)",

              border: "1px solid rgba(13,110,253,0.08)",
              borderRadius: "20px",

              boxShadow: "0 12px 40px rgba(15,23,42,0.08)",

              display: "flex",
              justifyContent: "space-between",
              overflow: "hidden",

              position: "relative",

              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 0,
                width: "120px",
                height: "100%",
                background: "#2C5FBE",
                clipPath: "polygon(0 0, 85% 0, 60% 100%, 0% 100%)",
                opacity: 0.08,
              },
            }}
          >
            {/* LOGO */}
            <Box
              onClick={goHome}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                zIndex: 2,
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  bgcolor: "#2C5FBE",
                  transform: "skewX(-20deg)",
                  mr: 1.5,
                  borderRadius: "2px",
                }}
              />

              <Box
                component="img"
                src={logo}
                alt="FactuSysDR"
                sx={{
                  width: { xs: 110, md: 130 },
                  height: "auto",
                }}
              />
            </Box>

            {/* NAVIGATION */}
            {!isMobile && (
              <Stack
                direction="row"
                spacing={1.5}
                sx={{
                  alignItems: "center",
                  zIndex: 2,
                }}
              >
                <Button
                  onClick={() => scrollToSection(null)}
                  sx={navItemStyles}
                >
                  Inicio
                </Button>

                <Button
                  onClick={() => scrollToSection("features")}
                  sx={navItemStyles}
                >
                  Beneficios
                </Button>

                <Button
                  onClick={() => scrollToSection("modulos")}
                  sx={navItemStyles}
                >
                  Módulos
                </Button>

                <Button
                  onClick={() => scrollToSection("cta")}
                  sx={navItemStyles}
                >
                  Contacto
                </Button>
              </Stack>
            )}

            {/* ── ACCIONES ── */}
            <Stack
              direction="row"
              spacing={1.5}
              sx={{ alignItems: "center", zIndex: 2 }}
            >
              {/* Hamburguesa — solo mobile */}
              {isMobile && (
                <IconButton
                  onClick={() => setDrawerOpen(true)}
                  aria-label="Abrir menú"
                  sx={{
                    color: "#1A2B4A",
                    width: 40,
                    height: 40,
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}

              {/* Botón Contacto — WhatsApp CTA llamativo */}
              {!isMobile && (
                <MotionButton
                  component="a"
                  href="https://api.whatsapp.com/send/?phone=593985092679&text=Hola,%20quiero%20solicitar%20una%20demo%20gratuita%20de%20FactuSysDR"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ y: -2, scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  variant="contained"
                  sx={{
                    background:
                      "linear-gradient(135deg,#2C5FBE 0%,#1A3D8F 100%)",

                    borderRadius: "12px",

                    px: {
                      xs: 2.5,
                      md: 3.5,
                    },

                    py: 1.2,

                    textTransform: "none",

                    fontWeight: 750,

                    fontSize: {
                      xs: "0.85rem",
                      md: "0.95rem",
                    },

                    letterSpacing: "0.01em",

                    color: "#fff",

                    boxShadow: "0 8px 24px #fff",

                    "&:hover": {
                      background:
                        "linear-gradient(135deg,#214A96 0%,#162E6B 100%)",

                      boxShadow: "0 14px 36px rgba(44,95,190,.45)",
                    },
                  }}
                >
                  Solicita una demo gratis
                </MotionButton>
              )}
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      {/* ── DRAWER MOBILE ── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            background: "#FFFFFF",
            pt: 2,
            pb: 3,
            px: 2,
          },
        }}
      >
        {/* Header del Drawer */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            px: 1,
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="FactuSysDR"
            sx={{ width: 100, height: "auto" }}
          />
          <IconButton
            onClick={() => setDrawerOpen(false)}
            aria-label="Cerrar menú"
            sx={{ color: "#1A2B4A" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Navegación */}
        <List sx={{ px: 1 }}>
          {[
            { label: "Inicio", target: null },
            { label: "Beneficios", target: "features" },
            { label: "Módulos", target: "modulos" },
            { label: "Contacto", target: "cta" },
          ].map((item) => (
            <ListItem key={item.label} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => scrollToSection(item.target)}
                sx={{
                  borderRadius: "12px",
                  py: 1.5,
                  px: 2,
                  "&:hover": {
                    backgroundColor: "rgba(44,95,190,0.06)",
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: 600,
                    fontSize: "1rem",
                    color: "#334155",
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Box sx={{ flex: 1 }} />

        {/* WhatsApp CTA en el Drawer */}
        <Box sx={{ px: 2 }}>
          <Button
            component="a"
            href="https://api.whatsapp.com/send/?phone=593985092679&text=Hola,%20quiero%20solicitar%20una%20demo%20gratuita%20de%20FactuSysDR"
            target="_blank"
            rel="noopener noreferrer"
            fullWidth
            variant="contained"
            sx={{
              background: "linear-gradient(135deg,#2C5FBE 0%,#1A3D8F 100%)",
              borderRadius: "12px",
              py: 1.5,
              textTransform: "none",
              fontWeight: 750,
              fontSize: "0.95rem",
              color: "#fff",
              boxShadow: "0 8px 24px rgba(44,95,190,0.3)",
              "&:hover": {
                background: "linear-gradient(135deg,#214A96 0%,#162E6B 100%)",
              },
            }}
          >
            Solicita demo gratis
          </Button>
        </Box>
      </Drawer>
    </>
  );
}

const navItemStyles = {
  color: "#334155",
  fontWeight: 600,
  fontSize: "0.95rem",
  textTransform: "none",
  position: "relative",

  "&::after": {
    content: '""',
    position: "absolute",
    left: "50%",
    bottom: 6,
    width: 0,
    height: "2px",
    background: "#2C5FBE",
    transition: "all .3s ease",
    transform: "translateX(-50%)",
  },

  "&:hover": {
    background: "transparent",
    color: "#2C5FBE",
  },

  "&:hover::after": {
    width: "70%",
  },
};
